import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navbar from '../components/Navbar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatTime = (date) =>
  new Date(date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });

const fmtSessionDate = (iso) => {
  if (!iso) return '';
  const d   = new Date(iso);
  const now  = new Date();
  const today     = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const day       = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time      = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  if (day.getTime() === today.getTime())     return `Today · ${time}`;
  if (day.getTime() === yesterday.getTime()) return `Yesterday · ${time}`;
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
};

const buildWelcomeText = (name, courseName, targetRole) => {
  let msg = `Hi ${name || 'there'}! I'm your Study Assistant`;
  if (courseName) msg += ` for **${courseName}**`;
  msg += '. I can help you understand concepts, clear doubts, and prepare for your target role';
  if (targetRole) msg += ` as **${targetRole}**`;
  msg += '.\n\nWhat would you like to learn today?';
  return msg;
};

// ─── Code block with copy button ─────────────────────────────────────────────

const CodeBlock = ({ language, children }) => {
  const [copied, setCopied] = useState(false);
  const code      = String(children).replace(/\n$/, '');
  const multiline = code.includes('\n');

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="my-3 rounded-xl overflow-hidden border border-gray-700 text-sm">
      <div className="flex items-center justify-between bg-[#282c34] px-3 py-1.5">
        <span className="text-xs text-gray-400 font-mono">{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors select-none"
        >
          {copied ? (
            <span className="text-green-400">✓ Copied</span>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        showLineNumbers={multiline && code.split('\n').length > 3}
        customStyle={{ margin: 0, borderRadius: 0, fontSize: '0.8rem', lineHeight: '1.6' }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

// ─── Markdown renderer ────────────────────────────────────────────────────────

const mdComponents = {
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline ? (
      <CodeBlock language={match?.[1]}>{children}</CodeBlock>
    ) : (
      <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 font-mono text-[0.82em]" {...props}>
        {children}
      </code>
    );
  },
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-gray-900 mt-4 mb-2 pb-1 border-b border-gray-200">{children}</h1>
  ),
  h2: ({ children }) => <h2 className="text-sm font-bold text-gray-900 mt-3 mb-1.5">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold text-gray-800 mt-2 mb-1">{children}</h3>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 my-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 my-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,
  p:  ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary/40 pl-3 my-2 text-gray-600 italic text-sm">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="text-primary underline hover:text-primary/80 transition-colors">
      {children}
    </a>
  ),
  hr: () => <hr className="my-3 border-gray-200" />,
};

const MarkdownRenderer = ({ content }) => (
  <div className="text-sm text-gray-800 leading-relaxed">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {content}
    </ReactMarkdown>
  </div>
);

// ─── Typewriter animation ─────────────────────────────────────────────────────

const TypewriterText = ({ text, onScrollNeeded }) => {
  const words = useMemo(() => text.split(' '), [text]);
  const [count, setCount] = useState(0);
  const delay = Math.min(22, Math.max(5, 2000 / words.length));

  useEffect(() => {
    if (count >= words.length) return;
    const t = setTimeout(() => {
      setCount((c) => c + 1);
      onScrollNeeded?.();
    }, delay);
    return () => clearTimeout(t);
  }, [count, words.length, delay, onScrollNeeded]);

  return <MarkdownRenderer content={words.slice(0, count).join(' ')} />;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const TypingDots = () => (
  <div className="flex items-center gap-1 px-1 py-0.5">
    {[0, 150, 300].map((d, i) => (
      <span key={i} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
        style={{ animationDelay: `${d}ms` }} />
    ))}
  </div>
);

const AIIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
  </svg>
);

const MessageBubble = ({ msg, onScrollNeeded }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex items-end gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
          <AIIcon className="w-4 h-4 text-secondary" />
        </div>
      )}
      <div className={`max-w-[78%] lg:max-w-[68%] flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`px-4 py-3 rounded-2xl break-words ${
          isUser
            ? 'bg-primary text-white rounded-br-sm text-sm leading-relaxed whitespace-pre-wrap'
            : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
        }`}>
          {isUser ? (
            msg.text
          ) : msg.animate ? (
            <TypewriterText text={msg.text} onScrollNeeded={onScrollNeeded} />
          ) : (
            <MarkdownRenderer content={msg.text} />
          )}
        </div>
        <span className="text-[11px] text-gray-400 mt-1 mx-1">{formatTime(msg.timestamp)}</span>
      </div>
    </div>
  );
};

// ─── History Panel ────────────────────────────────────────────────────────────

const HistoryPanel = ({ sessions, currentSessionId, viewingSessionId, onClose, onSelectSession, onGoLive }) => {
  const pastSessions = sessions.filter((s) => s.session_id !== currentSessionId);

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Chat History</h3>
        <button
          onClick={onClose}
          className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Current / Live session item */}
      <div className="flex-shrink-0 px-3 pt-3 pb-2">
        <button
          onClick={onGoLive}
          className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition-colors ${
            viewingSessionId === null
              ? 'bg-primary/10 border border-primary/20'
              : 'hover:bg-gray-50 border border-transparent'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 animate-pulse" />
          <span className={`text-sm font-medium ${viewingSessionId === null ? 'text-primary' : 'text-gray-700'}`}>
            Current Chat
          </span>
          {viewingSessionId === null && (
            <span className="ml-auto text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
              Live
            </span>
          )}
        </button>
      </div>

      {/* Past sessions */}
      {pastSessions.length > 0 && (
        <div className="flex-shrink-0 px-4 pb-1.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Past Sessions</p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
        {pastSessions.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">No past sessions yet</p>
        ) : (
          pastSessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => onSelectSession(s.session_id)}
              className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors group ${
                viewingSessionId === s.session_id
                  ? 'bg-secondary/10 border border-secondary/20'
                  : 'hover:bg-gray-50 border border-transparent'
              }`}
            >
              <div className="text-[11px] text-gray-400 mb-0.5">{fmtSessionDate(s.started_at)}</div>
              <div className="text-sm text-gray-700 leading-snug line-clamp-2">{s.preview}</div>
              <div className="text-[11px] text-gray-400 mt-1">{s.message_count} messages</div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const StudyAssistant = () => {
  const navigate       = useNavigate();
  const { user }       = useAuth();
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);

  const [sessionId] = useState(() => crypto.randomUUID());

  // Core chat state
  const [loading,      setLoading]      = useState(true);
  const [notActivated, setNotActivated] = useState(false);
  const [context,      setContext]      = useState({ courseName: '', targetRole: '' });
  const [messages,     setMessages]     = useState([]);
  const [usage,        setUsage]        = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [inputText,    setInputText]    = useState('');
  const [sending,      setSending]      = useState(false);

  // History panel state
  const [showHistoryPanel,  setShowHistoryPanel]  = useState(false);
  const [sessions,          setSessions]          = useState([]);
  const [viewingSessionId,  setViewingSessionId]  = useState(null);   // null = live
  const [historyMessages,   setHistoryMessages]   = useState([]);
  const [historyLoading,    setHistoryLoading]    = useState(false);

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  // ── Initial data fetch ─────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      const [profileResult, usageResult, batchesResult, sessionsResult] =
        await Promise.allSettled([
          api.get('/api/ai/profile'),
          api.get('/api/ai/study/usage-today'),
          api.get('/api/batches'),
          api.get('/api/ai/study/sessions'),
        ]);

      if (
        usageResult.status === 'rejected' &&
        usageResult.reason?.response?.status === 403
      ) {
        setNotActivated(true);
        setLoading(false);
        return;
      }

      if (usageResult.status === 'fulfilled') {
        const u = usageResult.value.data;
        setUsage(u);
        if (u.remaining <= 0) setLimitReached(true);
      }

      if (sessionsResult.status === 'fulfilled') {
        setSessions(sessionsResult.value.data || []);
      }

      const targetRole =
        profileResult.status === 'fulfilled'
          ? profileResult.value.data.target_role || ''
          : '';

      let courseName = '';
      if (batchesResult.status === 'fulfilled') {
        const batches = batchesResult.value.data || [];
        if (batches.length > 0) {
          courseName = batches[0].course_title || batches[0].batch_name || '';
        }
      }

      setContext({ courseName, targetRole });
      setMessages([
        {
          id:        'welcome',
          role:      'ai',
          text:      buildWelcomeText(user?.full_name, courseName, targetRole),
          timestamp: new Date(),
          animate:   false,
        },
      ]);
      setLoading(false);
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && !notActivated && !limitReached && viewingSessionId === null) {
      inputRef.current?.focus();
    }
  }, [loading, notActivated, limitReached, viewingSessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, historyMessages, sending]);

  // ── History handlers ───────────────────────────────────────────────────────

  const handleViewSession = async (sid) => {
    setViewingSessionId(sid);
    setHistoryLoading(true);
    setHistoryMessages([]);
    try {
      const res = await api.get(`/api/ai/study/history/${sid}`);
      setHistoryMessages(
        res.data.map((m, i) => ({
          id:        m.id || `h-${i}`,
          role:      m.role === 'assistant' ? 'ai' : 'user',
          text:      m.message,
          timestamp: new Date(m.created_at),
          animate:   false,
        }))
      );
    } catch {
      setHistoryMessages([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleBackToLive = () => {
    setViewingSessionId(null);
    setHistoryMessages([]);
  };

  // ── Send handler ───────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending || limitReached) return;

    setMessages((prev) => [
      ...prev,
      { id: `u-${Date.now()}`, role: 'user', text, timestamp: new Date(), animate: false },
    ]);
    setInputText('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setSending(true);

    try {
      const res  = await api.post('/api/ai/study/chat', { message: text, session_id: sessionId });
      const data = res.data;

      setMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, role: 'ai', text: data.response, timestamp: new Date(), animate: true },
      ]);

      const newUsage = {
        usage_today: data.usage_today,
        usage_limit: data.usage_limit,
        remaining:   data.usage_limit - data.usage_today,
      };
      setUsage(newUsage);
      if (newUsage.remaining <= 0) setLimitReached(true);
    } catch (err) {
      const httpStatus = err.response?.status;
      if (httpStatus === 429) {
        setLimitReached(true);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`, role: 'ai', animate: false, timestamp: new Date(),
            text: "You've used all your questions for today. Resets tomorrow. Contact your trainer if you need more.",
          },
        ]);
      } else if (httpStatus === 403) {
        setNotActivated(true);
      } else {
        const detail = err.response?.data?.detail;
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`, role: 'ai', animate: false, timestamp: new Date(),
            text: detail || 'Something went wrong. Please try again.',
          },
        ]);
      }
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Usage bar ──────────────────────────────────────────────────────────────

  const usagePct = usage
    ? Math.min(100, (usage.usage_today / Math.max(1, usage.usage_limit)) * 100)
    : 0;
  const usageBarCls = usagePct >= 90 ? 'bg-red-400' : usagePct >= 60 ? 'bg-amber-400' : 'bg-primary';

  // Session label shown in the amber history-view banner
  const viewingSession = sessions.find((s) => s.session_id === viewingSessionId);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-screen bg-gray-50 overflow-hidden flex flex-col">
      <Navbar />

      <div className="flex-1 lg:ml-60 pt-14 lg:pt-0 flex flex-col min-h-0 overflow-hidden">

        {/* ── Loading ── */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm text-gray-500">Loading Study Assistant...</p>
            </div>
          </div>
        )}

        {/* ── Not Activated ── */}
        {!loading && notActivated && (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md w-full text-center">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">CareerAI Access Required</h2>
              <p className="text-sm text-gray-600 mb-6">
                Please contact your admin to activate CareerAI access for your account.
              </p>
              <button
                onClick={() => navigate('/student/careerai')}
                className="px-5 py-2 bg-secondary text-white text-sm font-medium rounded-xl hover:bg-secondary/90 transition-colors"
              >
                ← Back to CareerAI
              </button>
            </div>
          </div>
        )}

        {/* ── Main area: history panel + chat ── */}
        {!loading && !notActivated && (
          <div className="flex-1 flex min-h-0 overflow-hidden relative">

            {/* ── History Panel ── */}
            {showHistoryPanel && (
              <>
                {/* Mobile backdrop */}
                <div
                  className="lg:hidden absolute inset-0 bg-black/20 z-10"
                  onClick={() => setShowHistoryPanel(false)}
                />
                <div className="absolute lg:relative inset-y-0 left-0 z-20 w-72 lg:w-64 flex-shrink-0 border-r border-gray-200 shadow-lg lg:shadow-none">
                  <HistoryPanel
                    sessions={sessions}
                    currentSessionId={sessionId}
                    viewingSessionId={viewingSessionId}
                    onClose={() => setShowHistoryPanel(false)}
                    onSelectSession={handleViewSession}
                    onGoLive={handleBackToLive}
                  />
                </div>
              </>
            )}

            {/* ── Chat Area ── */}
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

              {/* ─ Top bar ─ */}
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 lg:px-6 py-3 flex items-center gap-3">

                {/* History toggle */}
                <button
                  onClick={() => setShowHistoryPanel((v) => !v)}
                  title="Chat history"
                  className={`flex items-center gap-1.5 text-sm transition-colors flex-shrink-0 px-2 py-1 rounded-lg ${
                    showHistoryPanel
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="hidden sm:inline text-xs font-medium">History</span>
                </button>

                <div className="w-px h-5 bg-gray-200 flex-shrink-0" />

                {/* Back to CareerAI */}
                <button
                  onClick={() => navigate('/student/careerai')}
                  className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back
                </button>

                <div className="w-px h-5 bg-gray-200 flex-shrink-0" />

                {/* Context info */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0 text-sm">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <svg className="w-4 h-4 text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    {context.courseName ? (
                      <span className="font-semibold text-secondary truncate">{context.courseName}</span>
                    ) : (
                      <span className="text-gray-400 italic">No course enrolled</span>
                    )}
                  </div>
                  {context.targetRole && (
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-gray-600">
                        Target: <span className="font-medium text-primary">{context.targetRole}</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* ─ History-view banner ─ */}
              {viewingSessionId !== null && (
                <div className="flex-shrink-0 bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-amber-800 truncate">
                      Read-only · Past session
                      {viewingSession ? ` from ${fmtSessionDate(viewingSession.started_at)}` : ''}
                    </span>
                  </div>
                  <button
                    onClick={handleBackToLive}
                    className="text-xs font-medium text-primary hover:underline whitespace-nowrap flex-shrink-0"
                  >
                    Return to live chat →
                  </button>
                </div>
              )}

              {/* ─ Usage bar (live mode only) ─ */}
              {viewingSessionId === null && usage && (
                <div className="flex-shrink-0 bg-white border-b border-gray-100 px-4 lg:px-6 py-2 flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                    Today: {usage.usage_today} / {usage.usage_limit}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`${usageBarCls} rounded-full h-1.5 transition-all duration-500`}
                      style={{ width: `${usagePct}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium whitespace-nowrap ${limitReached ? 'text-red-500' : 'text-gray-400'}`}>
                    {limitReached ? 'Limit reached' : `${usage.remaining} left`}
                  </span>
                </div>
              )}

              {/* ─ Messages area ─ */}
              <div className="flex-1 overflow-y-auto px-4 lg:px-8 py-5 space-y-4">
                {viewingSessionId !== null ? (
                  // History view
                  historyLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <svg className="animate-spin w-6 h-6 text-primary" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    </div>
                  ) : historyMessages.length === 0 ? (
                    <p className="text-center text-sm text-gray-400 mt-10">No messages found in this session.</p>
                  ) : (
                    historyMessages.map((msg) => (
                      <MessageBubble key={msg.id} msg={msg} onScrollNeeded={scrollToBottom} />
                    ))
                  )
                ) : (
                  // Live chat
                  <>
                    {messages.map((msg) => (
                      <MessageBubble key={msg.id} msg={msg} onScrollNeeded={scrollToBottom} />
                    ))}
                    {sending && (
                      <div className="flex items-end gap-2">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                          <AIIcon className="w-4 h-4 text-secondary" />
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3">
                          <TypingDots />
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* ─ Input / read-only footer ─ */}
              {viewingSessionId !== null ? (
                <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50 px-4 py-2.5 text-center">
                  <span className="text-xs text-gray-400">
                    Read-only view ·{' '}
                    <button onClick={handleBackToLive} className="text-primary hover:underline">
                      Return to live chat
                    </button>
                  </span>
                </div>
              ) : (
                <div className="flex-shrink-0 border-t border-gray-200 bg-white px-4 lg:px-6 py-3">
                  {limitReached ? (
                    <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm text-amber-800">
                        You've used all your questions for today. Resets tomorrow.
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-end gap-2">
                      <textarea
                        ref={inputRef}
                        value={inputText}
                        onChange={(e) => {
                          setInputText(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 128)}px`;
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask me anything about your course..."
                        disabled={sending}
                        rows={1}
                        className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors disabled:opacity-50 disabled:bg-gray-50 overflow-y-auto"
                        style={{ lineHeight: '1.5', maxHeight: '128px' }}
                      />
                      <button
                        onClick={handleSend}
                        disabled={!inputText.trim() || sending}
                        className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Send (Enter)"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {!limitReached && (
                    <p className="text-[11px] text-gray-400 mt-1.5 ml-1">
                      <span className="font-medium">Enter</span> to send
                      &nbsp;·&nbsp;
                      <span className="font-medium">Shift+Enter</span> for new line
                    </p>
                  )}
                </div>
              )}
            </div>
            {/* end Chat Area */}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyAssistant;
